import { postToolResponse } from '../api/tool-response'
import { useAuth } from '../providers'
import { getQueueForChat } from '../store'
import { kickContinuation } from './submit-flow'
import { useSubmitMessage } from './useSubmitMessage'

/**
 * Posts a client-resolved tool result (e.g. an AskUserQuestion answer) back to
 * the conversation and resumes the turn. Returns the raw Response — it does
 * NOT throw on a non-OK status, so callers must check `response.ok`.
 */
export const useSubmitToolResponse = () => {
  const { getAuthToken } = useAuth()
  const submitMessage = useSubmitMessage()

  return async (conversationId: string, toolCallId: string, toolResponse: string) => {
    const accessToken = await getAuthToken()
    const response = await postToolResponse({
      accessToken,
      conversationId,
      toolCallId,
      toolResponse,
    })

    if (response.ok) {
      // Resume the turn in the conversation the tool result belongs to — not
      // whatever chat happens to be active. `kickContinuation` owns the
      // queue-gate (queued sends consume the result as they drain) and an
      // actively-streaming turn with an EMPTY queue must kick: that turn's
      // context was assembled before the answer landed, and with nothing
      // queued no future turn would ever consume it (submitMessage queues the
      // kick and it drains when the stream ends). Deliberately NOT awaited:
      // the answer is already persisted by the POST above, so the caller (the
      // AskUserQuestion card) can flip to "Answered" and drop its Submit
      // spinner immediately rather than waiting out the whole follow-up
      // stream. A failure here must not reject the caller (it would surface
      // as a submit failure and a retry would duplicate the tool-result
      // POST), so swallow + log it.
      void kickContinuation(getQueueForChat, submitMessage, conversationId).catch((error) => {
        console.error('Failed to continue chat after tool response:', error)
      })
    }

    return response
  }
}
